/**
 * Notification Category Filter
 *
 * Horizontal scrollable chip row for filtering notifications by category.
 * Active chip uses emerald brand color.
 *
 * @module components/notifications/NotificationCategoryFilter
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import type { NotificationCategory } from '../../entities/notification/types';

interface FilterOption {
  value: NotificationCategory | 'ALL';
  i18nKey: string;
}

const FILTER_OPTIONS: FilterOption[] = [
  { value: 'ALL', i18nKey: 'categories.all' },
  { value: 'CAMPAIGNS', i18nKey: 'categories.campaigns' },
  { value: 'TRANSACTIONS', i18nKey: 'categories.transactions' },
  { value: 'SECURITY', i18nKey: 'categories.security' },
  { value: 'SYSTEM', i18nKey: 'categories.system' },
  { value: 'ONBOARDING', i18nKey: 'categories.setup' },
  { value: 'MARKETING', i18nKey: 'categories.marketing' },
];

interface NotificationCategoryFilterProps {
  activeCategory: NotificationCategory | 'ALL';
  onCategoryChange: (category: NotificationCategory | 'ALL') => void;
}

/**
 * Horizontal chip row for category filtering
 */
const NotificationCategoryFilter: React.FC<NotificationCategoryFilterProps> = ({
  activeCategory,
  onCategoryChange,
}) => {
  const { t } = useTranslation('notifications');

  return (
    <div className="flex gap-1.5 overflow-x-auto px-4 py-2 scrollbar-hide">
      {FILTER_OPTIONS.map((option) => {
        const isActive = activeCategory === option.value;
        return (
          <button
            key={option.value}
            onClick={() => onCategoryChange(option.value)}
            className={`shrink-0 rounded-full px-3 py-1 text-xs font-medium transition-colors duration-150 ${
              isActive
                ? 'bg-emerald-600 text-white shadow-xs'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-gray-700'
            }`}
            aria-pressed={isActive}
          >
            {t(option.i18nKey)}
          </button>
        );
      })}
    </div>
  );
};

export default NotificationCategoryFilter;
